import { ReactNode, useCallback, useState } from "react";
import { useSelector } from "react-redux";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";
// Layouts
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Components
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import { RepairOrdersBody } from "./Tabs/RepairOrders/RepairOrdersBody";
import TFTModel from "@DataPages/components/ServiceOperations/Tabs/TechnicianFlaggedTime/TFTModel";
import AdvisorPerformanceAnalyticsSectionLabel from "@DataPages/components/ServiceOperations/Tabs/AdvisorPerformance/components/AdvisorPerformanceAnalyticsSectionLabel";
import AdvisorPerformanceModel from "@DataPages/components/ServiceOperations/Tabs/AdvisorPerformance/AdvisorPerformanceModel";
import TFTAnalyticsSectionLabel from "@DataPages/components/ServiceOperations/Tabs/TechnicianFlaggedTime/components/TFTAnalyticsSectionLabel";

// Constants
import actionsList from "@Constants/actionsList";

// Types
import { ILocationState } from "@/Common/interfaces/location";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

enum ETabLabel {
	RepairOrders = "Repair Orders",
	AdvisorPerformance = "Advisor Performance",
	TechnicianFlaggedTime = "Technician Flagged Time",
}

interface IRepairOrdersLayerProps {
	periodTreeSelection: IPeriodTreeSelection;
}
interface TabInfo {
	index: number;
	title: ETabLabel;
}

function getTabIndex(tabLabel: ETabLabel): number {
	return Object.values(ETabLabel).indexOf(tabLabel);
}

export function ServiceOperationsModel(props: Readonly<IRepairOrdersLayerProps>) {
	const { periodTreeSelection } = props;
	const { useAuth } = useAuthorization();

	const payPeriodTitle = periodTreeSelection?.titleExtended?.split(" ").slice(0, -1).join(" ") ?? "";
	const commissionCycleId = periodTreeSelection?.cycleId ?? "";
	const payPeriodId = periodTreeSelection?.payPeriodId ?? "";

	const location = useSelector((state: ILocationState) => state.location.location);
	const locationId = location?.locationId;

	const hasRepairOrdersAccess = useAuth(actionsList.VIEW_REPAIR_ORDERS);
	const hasAdvisorPerformanceAccess = useAuth(actionsList.VIEW_ADVISOR_PERFORMANCE);
	const hasTechnicianFlaggedTimeAccess = useAuth(actionsList.VIEW_TECHNICIAN_FLAGGED_TIME);

	/********************************************* HELPERS ************************************************/
	const isTabEnabled = useCallback(
		(tab: ETabLabel): boolean => {
			switch (tab) {
				case ETabLabel.RepairOrders: // Repair Orders
					return hasRepairOrdersAccess;
				case ETabLabel.AdvisorPerformance: // Advisor Performance
					return hasAdvisorPerformanceAccess;
				case ETabLabel.TechnicianFlaggedTime: // Technician Flagged Time
					return hasTechnicianFlaggedTimeAccess;
				default:
					return false;
			}
		},
		[hasRepairOrdersAccess, hasAdvisorPerformanceAccess, hasTechnicianFlaggedTimeAccess]
	);

	const firstTabWithAccess = Object.values(ETabLabel).find(isTabEnabled) ?? ETabLabel.RepairOrders;

	// States
	const [tabSelected, setTabSelected] = useState<TabInfo>(() => ({
		title: firstTabWithAccess,
		index: getTabIndex(firstTabWithAccess),
		disabled: !isTabEnabled(firstTabWithAccess),
	}));

	const tabs = Object.values(ETabLabel)
		.filter(Boolean)
		.map((value) => ({ title: value, index: getTabIndex(value), disabled: !isTabEnabled(value) }));

	return (
		<>
			<RoundedBox className="!p-0 !pt-8 !h-fit mb-4">
				<TabSelectable
					tabs={tabs}
					tabBarClassName={"!p-0 !m-0"}
					tabsProps={{
						tabClasses: "!min-w-[1100px]",
					}}
					content={[
						<RepairOrdersBody
							key={ETabLabel.RepairOrders}
							periodTreeSelection={periodTreeSelection}
							payPeriodTitle={payPeriodTitle}
							locationId={locationId}
							commissionCycleId={commissionCycleId}
							payPeriodId={payPeriodId}
							isTabSelected={tabSelected.title === ETabLabel.RepairOrders}
							hasPermission={hasRepairOrdersAccess}
						/>,

						<AdvisorPerformanceAnalyticsSectionLabel key={ETabLabel.AdvisorPerformance} />,
						<TFTAnalyticsSectionLabel key={ETabLabel.TechnicianFlaggedTime} />,
					]}
					tabSelected={tabSelected.index}
					onTabChange={(_, tabInfo) => {
						setTabSelected(tabInfo.label);
					}}
				/>
			</RoundedBox>
			{tabSelected.title === ETabLabel.AdvisorPerformance && (
				<ServiceOperationBottomComponent>
					<AdvisorPerformanceModel
						selected={periodTreeSelection}
						isTabSelected={tabSelected.title === ETabLabel.AdvisorPerformance}
						hasPermission={hasAdvisorPerformanceAccess}
					/>
				</ServiceOperationBottomComponent>
			)}

			{tabSelected.title === ETabLabel.TechnicianFlaggedTime && (
				<ServiceOperationBottomComponent>
					<TFTModel
						selected={periodTreeSelection}
						isTabSelected={tabSelected.title === ETabLabel.TechnicianFlaggedTime}
						hasPermission={hasTechnicianFlaggedTimeAccess}
					/>
				</ServiceOperationBottomComponent>
			)}
		</>
	);
}

function ServiceOperationBottomComponent({ children }: { children: ReactNode }) {
	return <RoundedBox className="!h-fit !min-h-[420px]">{children}</RoundedBox>;
}
